import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import { WifiOff } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { PaymentMethodChooser } from './PaymentMethodChooser';
import { CashPanel } from './CashPanel';
import { EmoneyPanel } from './EmoneyPanel';
import { OnlineQrisPanel } from './OnlineQrisPanel';
import { qrisViewOf } from '@/lib/onlinePayment';
import { EdcPanel } from './EdcPanel';
import { usePaymentMethods } from '@/hooks/queries';
import { useCreateSale } from '@/hooks/mutations';
import { useOnline } from '@/hooks/useOnline';
import { buildSalePayload } from '@/lib/buildSalePayload';
import { useCartStore } from '@/stores/cart.store';
import { useDeviceStore } from '@/stores/device.store';
import type { PaymentMethod } from '@/types/payment';
import type { CreateSalePayload, CreateSaleResult, SaleStatus, SocketConfig } from '@/types/sale';
import { getSaleQuote } from '@/api/sale.api';
import { resolveAmountDue } from '@/lib/amountDue';
import type { ApiError } from '@/types/api';
import { edcCopyFor } from './edcTenderCopy';
import { visitDateErrorMessage } from '@/lib/visitDateErrors';
import { focErrorMessage } from '@/lib/focDocument';

type Stage = 'choose' | 'cash' | 'emoney' | 'qris' | 'edc';

export const PROMO_QUOTA_TAKEN_MESSAGE =
  'Kuota promo baru saja habis. Harga sudah diperbarui — cek totalnya, lalu terima pembayaran lagi.';

interface PaymentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Fired when a sale reaches SUCCESS — hands the invoiceId to the print flow. */
  onPaid: (result: CreateSaleResult) => void;
  /** Capacity 409 — bounce back to the date/slot picker. */
  onCapacityBlocked?: () => void;
  /**
   * Visit-date rejection (409 VISIT_DATE_BLOCKED or 400 VISIT_DATE_*). The
   * dialog closes and hands over the cashier-facing message; NOT a capacity
   * bounce — the caller keeps the cart and locks payment.
   */
  onVisitDateRejected?: (message: string, error: ApiError) => void;
  /**
   * Free-of-charge rejection (FOC_DOCUMENT_* / FOC_CART_INVALID). No sale was
   * created; the dialog closes and hands over the cashier-facing message so the
   * caller can send the cashier back to fix the document or cart.
   */
  onFocRejected?: (message: string, error: ApiError) => void;
  /**
   * 409 PROMO_QUOTA_TAKEN: a promotion this cart used just ran out (#2107). No
   * sale was created and the dialog closes; the caller prices the cart again.
   */
  onPromoQuotaTaken?: () => void;
  /**
   * The cart's quoted total (incl. discounts + rate plan), priced with a
   * placeholder method. Only a last resort here: once a tender is chosen the
   * dialog shows that tender's own quote, then the sale's charge — see
   * `lib/amountDue`. Shown only if the per-tender quote fails.
   */
  total: number;
}

export function PaymentDialog({
  open,
  onOpenChange,
  onPaid,
  onCapacityBlocked,
  onVisitDateRejected,
  onFocRejected,
  onPromoQuotaTaken,
  total,
}: PaymentDialogProps) {
  const [stage, setStage] = useState<Stage>('choose');
  /**
   * True while the EDC terminal still holds a live transaction.
   *
   * The ECR protocol has no cancel, so dismissing this dialog does NOT stop the
   * terminal: it keeps the card slot open or the QR on screen, and a customer
   * who then pays gets a sale that settles and mints a wristband the cashier
   * believes was cancelled. The panel already withholds a cancel button for
   * that reason; this closes the other two exits.
   */
  const [terminalLive, setTerminalLive] = useState(false);
  const [method, setMethod] = useState<PaymentMethod | null>(null);
  const [socketConfig, setSocketConfig] = useState<SocketConfig | null>(null);
  const [invoiceId, setInvoiceId] = useState<string | null>(null);
  const [qrString, setQrString] = useState<string | null>(null);
  // The server-resolved QR image. Only ASPI needs it: an ASPI border exists only
  // in Midtrans' rendered PNG, so a QR we draw ourselves cannot carry one.
  const [qrImageUrl, setQrImageUrl] = useState<string | null>(null);
  // A Snap charge: Midtrans's own page is embedded where the QR would be.
  const [snapToken, setSnapToken] = useState<string | null>(null);
  const [paymentExpire, setPaymentExpire] = useState<string | null>(null);
  const [qrisStatus, setQrisStatus] = useState<SaleStatus>('PENDING');
  // Who took the charge (Midtrans / Xendit / Doku) — shown under the code.
  const [qrisGateway, setQrisGateway] = useState<string | null>(null);
  // The chosen method's quote body, and what the created sale charged.
  const [quotePayload, setQuotePayload] = useState<CreateSalePayload | null>(null);
  const [chargedTotal, setChargedTotal] = useState<number | null>(null);

  const { data: methods = [] } = usePaymentMethods();
  const reader = useDeviceStore((s) => s.reader);
  const { mutate: createSale, isPending } = useCreateSale();
  const online = useOnline();
  // One idempotency key per checkout session — reused across retries of the
  // same cart so a flaky-network retry can't double-create the sale.
  const idemKeyRef = useRef<string>('');

  // Reset on (re)open.
  useEffect(() => {
    if (open) {
      setStage('choose');
      setMethod(null);
      setSocketConfig(null);
      setInvoiceId(null);
      setQrString(null);
      setQrImageUrl(null);
      setSnapToken(null);
      setPaymentExpire(null);
      setQrisStatus('PENDING');
      setQuotePayload(null);
      setChargedTotal(null);
      idemKeyRef.current = crypto.randomUUID();
    }
  }, [open]);

  // The cart quote behind `total` is priced with a placeholder method, so a
  // PAYMENT-gated discount is wrong there for any other tender. Re-price for the
  // tender chosen, then hold the sale's own charge once it exists.
  const methodQuote = useQuery({
    queryKey: ['saleQuote', quotePayload],
    queryFn: () => getSaleQuote(quotePayload!),
    enabled: open && quotePayload !== null && chargedTotal === null,
    staleTime: 10_000,
    retry: false,
  });
  const amountDue = resolveAmountDue({
    charged: chargedTotal,
    methodQuote: methodQuote.data?.total,
    methodQuoteFailed: methodQuote.isError,
    cartTotal: total,
  });

  // Every tender's price, shown on the chooser. Same body and key as
  // `methodQuote`, so choosing a method reuses the figure on its button.
  // `payloadFor` reads the cart when called; the cart cannot change while this
  // dialog is open, so `open` stands in for it.
  const chooserPayloads = useMemo(
    () => (open ? methods.map((m) => payloadFor(m)) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [open, methods, reader],
  );
  const chooserQuotes = useQueries({
    queries: chooserPayloads.map((payload) => ({
      queryKey: ['saleQuote', payload],
      queryFn: () => getSaleQuote(payload),
      enabled: stage === 'choose',
      staleTime: 10_000,
      retry: false,
    })),
  });
  const chooserPrices: Record<string, number | 'pending'> = {};
  chooserQuotes.forEach((q, i) => {
    const id = methods[i]?.id;
    if (!id || q.isError) return;
    if (typeof q.data?.total === 'number') chooserPrices[id] = q.data.total;
    else if (q.isPending) chooserPrices[id] = 'pending';
  });

  /** The sale body for method `m` — what the quote prices and the sale posts. */
  function payloadFor(m: PaymentMethod, amountTendered?: number) {
    const cartState = useCartStore.getState();
    return buildSalePayload({
      lines: cartState.lines,
      bundleLines: cartState.bundleLines,
      customer: cartState.customer,
      visitDate: cartState.visitDate,
      couponCode: cartState.couponCode,
      discountIds: cartState.discountIds,
      paymentMethodId: m.id,
      paymentGateway: m.paymentGateway,
      amountTendered,
      reader: m.paymentGateway === 'e-money' ? reader : undefined,
      groupType: cartState.promo.groupType,
      sourceChannel: cartState.promo.sourceChannel,
      identity: cartState.promo.identity,
      documentScanToken: cartState.promo.documentScanToken,
      focDocumentId: cartState.mode === 'foc' ? cartState.focDocument?.id : null,
    });
  }

  /** Create the sale for the chosen method. `amountTendered` only for cash. */
  function submitFor(m: PaymentMethod, amountTendered?: number) {
    // Never fire a payment while offline — the sale reserves capacity + settles
    // server-side, so a queued submit could land in a sold-out slot or a failed
    // charge after the customer has left. Block clearly instead.
    if (!online) {
      toast.error('Koneksi terputus — transaksi tidak dapat diproses. Sambungkan kembali internet.');
      return;
    }
    const payload = payloadFor(m, amountTendered);

    createSale({ payload, idempotencyKey: idemKeyRef.current }, {
      onSuccess: (result) => {
        setInvoiceId(result.invoiceId);
        // What the terminal / reader / QR was actually asked for.
        const charged = Number(result.totalAmount);
        if (result.totalAmount != null && Number.isFinite(charged)) setChargedTotal(charged);
        if (m.paymentGateway === 'cash') {
          onPaid(result);
        } else if (m.paymentGateway === 'e-money') {
          if (result.socket) setSocketConfig(result.socket);
          else toast.error('Server tidak mengirim konfigurasi pembaca kartu.');
        } else if (m.paymentGateway === 'edc-permata') {
          // `socket.room` IS the PaymentTransaction id — the handle needed to
          // resolve the payment later if the terminal never answers.
          if (result.socket) setSocketConfig(result.socket);
          else toast.error('Server tidak mengirim status mesin EDC. Cek mesin sebelum mengulang.');
        } else {
          // ONLY a real QRIS payload. `qr_string` is what a Midtrans QRIS *charge*
          // returns; `token` is the Snap shape and was used as a last fallback
          // here. A Snap token is an opaque session id with no merchant PAN and
          // no CRC, so drawing it as a QR produced a code no banking app can
          // pay — the customer scanned it at the counter and nothing happened.
          // The panel now says the code is unavailable instead of faking one.
          // `qrisViewOf` reads the gateway-neutral `payment` block first (any
          // processor), then Midtrans's own fields for an older response.
          const view = qrisViewOf(result);
          setQrString(view.qrString);
          setQrImageUrl(view.qrImageUrl);
          setSnapToken(view.snapToken);
          setPaymentExpire(view.paymentExpire);
          setQrisGateway(view.gateway);
          // QRIS now gets a channel too, so the cashier sees the payment land
          // instead of waiting for the next poll. Its absence is survivable —
          // the panel keeps polling — so unlike e-money and EDC this does not
          // warn the cashier.
          if (result.socket) setSocketConfig(result.socket);
          setQrisStatus(result.status ?? 'PENDING');
        }
      },
      onError: (err) => {
        const apiErr = err as ApiError;
        // Visit-date rejections first: a blocked date also answers 409 with
        // `blocked: true`, exactly like a full slot — only `errorCode` tells
        // them apart. No sale was created, so closing the dialog is safe.
        const dateMsg = visitDateErrorMessage(apiErr);
        if (dateMsg) {
          toast.error(dateMsg);
          backToChoose();
          onOpenChange(false);
          onVisitDateRejected?.(dateMsg, apiErr);
          return;
        }
        // FOC rejections next: FOC_DOCUMENT_ALREADY_USED answers 409 too, and
        // must not be mistaken for a full slot (which bounces to the catalog).
        const focMsg = focErrorMessage(apiErr);
        if (focMsg) {
          toast.error(focMsg);
          backToChoose();
          onOpenChange(false);
          onFocRejected?.(focMsg, apiErr);
          return;
        }
        // A promotion's last quota went to another order a moment earlier
        // (#2107). Also a 409, but not a full slot: no sale was made, the cart
        // stays, and it is priced again before the cashier takes payment.
        if (apiErr?.errorCode === 'PROMO_QUOTA_TAKEN') {
          toast.warning(PROMO_QUOTA_TAKEN_MESSAGE);
          backToChoose();
          onOpenChange(false);
          onPromoQuotaTaken?.();
          return;
        }
        const blocked =
          apiErr?.code === 409 || (apiErr?.payload as { blocked?: boolean })?.blocked === true;
        toast.error(apiErr?.message ?? 'Gagal membuat transaksi.');
        if (m.paymentGateway !== 'cash') setStage('choose');
        if (blocked && onCapacityBlocked) {
          onOpenChange(false);
          onCapacityBlocked();
        }
      },
    });
  }

  function choose(m: PaymentMethod) {
    setMethod(m);
    setChargedTotal(null);
    setQuotePayload(payloadFor(m));
    if (m.paymentGateway === 'cash') {
      setStage('cash');
    } else if (m.paymentGateway === 'e-money') {
      setStage('emoney');
      submitFor(m); // immediately create sale to obtain the socket config
    } else if (m.paymentGateway === 'edc-permata') {
      setStage('edc');
      submitFor(m); // the sale is committed BEFORE the terminal is asked
    } else {
      setStage('qris');
      submitFor(m);
    }
  }

  /**
   * Return to the chooser AND mint a new idempotency key, so the next attempt
   * creates a real new sale instead of replaying the cached response of the
   * last one.
   *
   * Only ever called where the SERVER said it is safe (`retrySafe`) — i.e.
   * something authoritative stated no money moved: the gateway's own word for
   * midtrans, `edc.state` for EDC. It is never true for e-money, which cannot
   * tell an unreachable reader from a declined tap. The client does not make
   * that judgment; it only acts on it.
   */
  function restartWithFreshKey() {
    idemKeyRef.current = crypto.randomUUID();
    backToChoose();
  }

  function backToChoose() {
    setStage('choose');
    setMethod(null);
    setQuotePayload(null);
    setChargedTotal(null);
    setSocketConfig(null);
    setQrString(null);
    setQrImageUrl(null);
    setSnapToken(null);
    setPaymentExpire(null);
    setQrisStatus('PENDING');
  }

  return (
    <Dialog open={open} onOpenChange={(next) => (terminalLive && !next ? undefined : onOpenChange(next))}>
      <DialogContent
        className="max-w-2xl"
        hideClose={stage !== 'choose'}
        // Hiding the ✕ is not enough: Radix dismisses on Escape and on an
        // outside click by default, which is how a cashier cancelled a payment
        // the terminal was still waiting on. The transaction is cancelled AT
        // THE TERMINAL, or not at all.
        onEscapeKeyDown={(e) => terminalLive && e.preventDefault()}
        onInteractOutside={(e) => terminalLive && e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>
            {stage === 'choose'
              ? 'Pilih metode pembayaran'
              : stage === 'cash'
                ? 'Pembayaran tunai'
                : stage === 'emoney'
                  ? 'Pembayaran e-money'
                  : stage === 'edc'
                    ? edcCopyFor(method?.paymentMethod).dialogTitle
                    : 'Pembayaran QRIS'}
          </DialogTitle>
          <DialogDescription>
            {stage === 'choose'
              ? 'Pilih bagaimana pelanggan akan membayar.'
              : 'Selesaikan pembayaran untuk mencetak tiket.'}
          </DialogDescription>
        </DialogHeader>

        {!online && (
          <div className="flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm font-medium text-destructive">
            <WifiOff className="h-4 w-4 shrink-0" aria-hidden="true" />
            Koneksi terputus — pembayaran ditunda sampai internet tersambung kembali.
          </div>
        )}

        {stage === 'choose' && (
          <PaymentMethodChooser
            methods={methods}
            selectedId={method?.id ?? null}
            onSelect={choose}
            prices={chooserPrices}
          />
        )}

        {stage === 'cash' && (
          <CashPanel
            total={amountDue}
            isSubmitting={isPending}
            onConfirm={(amt) => method && submitFor(method, amt)}
          />
        )}

        {stage === 'emoney' && (
          <EmoneyPanel
            total={amountDue}
            socketConfig={socketConfig}
            onSuccess={() => {
              if (invoiceId) onPaid({ invoiceId, status: 'SUCCESS' });
            }}
            onCancel={backToChoose}
            onRetry={() => method && submitFor(method)}
          />
        )}

        {stage === 'edc' && (
          <EdcPanel
            total={amountDue}
            // The gateway is `edc-permata` for BOTH tenders, so the panel
            // cannot work out which one it is on its own.
            tender={method?.paymentMethod === 'edc-qris' ? 'edc-qris' : 'edc-card'}
            onTerminalLiveChange={setTerminalLive}
            socketConfig={socketConfig}
            paymentTransactionId={socketConfig?.room ?? null}
            onSuccess={() => invoiceId && onPaid({ invoiceId, status: 'SUCCESS' })}
            onCancel={() => {
              backToChoose();
              onOpenChange(false);
            }}
            // A fresh key ONLY where the server proved no money moved
            // (DECLINED / FAILED_NOT_SENT). An UNRESOLVED terminal never
            // reaches here, but if it somehow did, this falls back to the
            // replay rather than risking a second charge.
            onRetry={(retrySafe) => (retrySafe ? restartWithFreshKey() : backToChoose())}
          />
        )}

        {stage === 'qris' && invoiceId && (
          <OnlineQrisPanel
            total={amountDue}
            invoiceId={invoiceId}
            qrString={qrString}
            qrImageUrl={qrImageUrl}
            snapToken={snapToken}
            paymentExpire={paymentExpire}
            socketConfig={socketConfig}
            status={qrisStatus}
            onStatus={setQrisStatus}
            onSuccess={() => onPaid({ invoiceId, status: 'SUCCESS' })}
            onCancel={backToChoose}
            onRestart={restartWithFreshKey}
            gateway={qrisGateway}
          />
        )}

        {stage === 'cash' && (
          <div className="flex justify-start">
            <Button variant="ghost" size="sm" onClick={backToChoose}>
              ← Ganti metode
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
